import type {
  ActivationMode,
  ExpiryMode,
  LandingSettings,
  NotificationMode,
  Recipient,
  ReminderEmailState,
  ScheduleMode,
  Stage3EmailState,
  UnboxingSettings,
} from "@/interfaces/CampaignBuilder";
import type { BuilderCampaignPayload } from "@/interfaces/campaignDraft";
import type { CauseSelection } from "@/interfaces/charity";
import { isAnonymousLinks } from "@/utils/campaignMode-helper";
import { normalizeCampaignName } from "@/utils/campaignName-helper";

/**
 * Client-side builder → wire payload serializer. Each function maps one step's slice of
 * CampaignBuilderContext to a partial BuilderCampaignPayload; the server's
 * campaign-builder-serializer flattens it into columns + child rows. Kept per-step so a
 * commit only sends the slice the user actually touched.
 */

/** Step 1 — budget + country filter + PER-COUNTRY curated grids (`feeds`, the server derives each
 *  country's diff). `feeds` is omitted when empty so an unrelated step save never wipes the
 *  campaign's feed_edits.
 *
 *  `internationalShipping` is NOT sent. The browser used to post its own isPro flag and the server
 *  stored it; the column is now derived server-side from the owning organization's plan. */
export function buildStep1Payload(
  selectedBudget: number | null,
  feeds: Record<string, string[]>,
  selectedCountry?: string,
  freezeGiftSelection?: boolean,
): BuilderCampaignPayload {
  const payload: BuilderCampaignPayload = {};
  if (selectedBudget != null) payload.budget = selectedBudget;
  if (selectedCountry) payload.country = selectedCountry;
  if (Object.keys(feeds).length > 0) payload.feeds = feeds;
  if (freezeGiftSelection !== undefined) payload.freezeGiftSelection = freezeGiftSelection;
  return payload;
}

interface Step2State {
  recipients: Recipient[];
  notificationMode: NotificationMode;
  scheduleMode: ScheduleMode;
  scheduleDate: string;
  scheduleTime: string;
  scheduleTz: string;
  expiryMode: ExpiryMode;
  customExpiryDate: string;
  // Manual gift links — the sender-distributes-links path. Sent regardless of mode (the server
  // only reads them when notificationMode === "manual") so a mode flip never strands old values.
  // Optional on the input: the real builder context always supplies them (with defaults), but a
  // gfg-only fixture/caller may omit them — an omitted value is simply dropped from the payload.
  linkToList?: boolean | null;
  linkQuantity?: number;
  senderFirstName?: string;
  senderLastName?: string;
  senderCompany?: string;
  senderSignature?: string;
  activationMode?: ActivationMode;
  activationDate?: string;
  activationTime?: string;
  activationTz?: string;
  // Reminder emails belong to Step 2: the count toggle lives in the Recipients step's
  // Gift Reminders accordion and the per-reminder editor (EmailReminderStation) is reached
  // from there — so they save on the same triggers as recipients/delivery.
  reminderCount: number;
  reminderEmails: Record<number, ReminderEmailState>;
}

/** Reminder emails slice of Step 2 — pulled out so a Step-1-only create (GFG-318) can seed the
 *  builder's default reminder count/content without duplicating this construction. */
export function buildRemindersPayload(
  reminderCount: number,
  reminderEmails: Record<number, ReminderEmailState>,
): Pick<BuilderCampaignPayload, "reminderCount" | "reminders"> {
  return {
    reminderCount,
    reminders: Array.from({ length: reminderCount }, (_, i) => {
      const r = reminderEmails[i];
      return {
        order: i + 1,
        subject: r?.subject,
        content: r?.messageHtml,
        showLogo: r?.showLogo,
        showBanner: r?.showBanner,
        // Undefined (the ordinary, untouched case) is dropped by JSON.stringify, so the server
        // never sees the key and leaves the auto-calculated default alone.
        sendDate: r?.sendDate,
        sendTime: r?.sendTime,
      };
    }),
  };
}

/** The reminder slice a draft CREATE may carry. Nothing until the plan's reminder cap is known:
 *  an unknown cap is not an unlimited one, and seeding the raw default of 4 while entitlements
 *  are still loading 402s the create on a plan capped below 4, pinning the user on Step 1. The
 *  Step 2 save writes the reminders once the cap has resolved. */
export function buildCreateReminderDefaults(
  capKnown: boolean,
  reminderCount: number,
  reminderEmails: Record<number, ReminderEmailState>,
): Partial<Pick<BuilderCampaignPayload, "reminderCount" | "reminders">> {
  return capKnown ? buildRemindersPayload(reminderCount, reminderEmails) : {};
}

/**
 * Step 2 — recipients + delivery config (recipients become contacts rows) + reminder emails.
 *
 * `editableOnly` is set once a campaign is past draft (payment freezes the recipient list and gift
 * reminders — server: STEP2_FROZEN_KEYS, which refuses them ON PRESENCE). A whole-step save then
 * carries only the three fields that stay editable after scheduling — send date, expiration, send
 * method — and the server's assertPostScheduleEditable judges each by DIFFERENCE, so re-sending an
 * unchanged value is a no-op rather than a 409. Omitting the frozen keys is what lets an ordinary
 * Step-2 save succeed on a scheduled/active campaign instead of tripping RECIPIENTS_LOCKED.
 */
export function buildStep2Payload(
  s: Step2State,
  opts?: { editableOnly?: boolean },
): BuilderCampaignPayload {
  const delivery: BuilderCampaignPayload = {
    notificationMode: s.notificationMode,
    scheduleMode: s.scheduleMode,
    scheduleDate: s.scheduleDate,
    scheduleTime: s.scheduleTime,
    scheduleTz: s.scheduleTz,
    expiryMode: s.expiryMode,
    customExpiryDate: s.customExpiryDate,
  };
  if (opts?.editableOnly) return delivery;
  // A recipient's own send date/time only under "Send on different dates": a sender who switched to
  // Send ASAP / Schedule for later would otherwise have each contact sent on its old date (GFG-443;
  // the API now ignores them there too). Switching back before a save finds them in the state; once
  // a save under another schedule lands, they are gone, as that schedule sends everyone together.
  const perRecipient = s.scheduleMode === "different";
  // Anonymous links (quantity-only manual, universal) have no recipient list: the stored rows stay in
  // the client state (and the DB) so switching back restores them, but the key is omitted so none are
  // sent or validated (GFG-442). Omitted, not `undefined`, so the server sees it absent.
  const recipients = isAnonymousLinks(s.notificationMode, s.linkToList ?? null)
    ? {}
    : {
        recipients: s.recipients.map((r) => ({
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email,
          toCompany: r.toCompany,
          recipientCountry: r.recipientCountry,
          senderFirst: r.senderFirst,
          senderLast: r.senderLast,
          senderCompany: r.senderCompany,
          fromEmail: r.fromEmail,
          senderSignature: r.senderSignature,
          sendDate: perRecipient ? r.sendDate : undefined,
          sendTime: perRecipient ? r.sendTime : undefined,
        })),
      };
  return {
    ...recipients,
    ...delivery,
    // Manual gift links (sent regardless of mode — the server reads them only when manual; the
    // recipient rows above are the part that is withheld in quantity-only mode).
    linkToList: s.linkToList,
    linkQuantity: s.linkQuantity,
    senderFirstName: s.senderFirstName,
    senderLastName: s.senderLastName,
    senderCompany: s.senderCompany,
    senderSignature: s.senderSignature,
    activationMode: s.activationMode,
    activationDate: s.activationDate,
    activationTime: s.activationTime,
    activationTz: s.activationTz,
    ...buildRemindersPayload(s.reminderCount, s.reminderEmails),
  };
}

interface Step3State {
  email: Stage3EmailState;
  unboxing: UnboxingSettings;
  landing: LandingSettings;
  /**
   * Self-send (manual/universal) has no email, so its design step must NOT write the shared
   * `campaigns.signature` column — Universal's sender signature lives there. Omits the email
   * signature from the payload so a Step-3 save can't clobber it.
   */
  omitEmailSignature?: boolean;
}

/** Step 3 — email + unboxing + landing design. (Reminder emails moved to Step 2.) */
export function buildStep3Payload(s: Step3State): BuilderCampaignPayload {
  const { email, unboxing, landing } = s;
  return {
    email: {
      subject: email.subject,
      messageHtml: email.messageHtml,
      signatureHtml: s.omitEmailSignature ? undefined : email.signatureHtml,
      ctaColor: email.ctaColor,
      logoEnabled: email.logoEnabled,
      logoUrl: email.logoUrl,
      logoOriginalUrl: email.logoOriginalUrl,
      logoPadding: email.logoPadding,
      bannerEnabled: email.bannerEnabled,
      bannerUrl: email.bannerUrl,
      bannerOriginalUrl: email.bannerOriginalUrl,
    },
    unboxing: {
      tokenFirstName: unboxing.tokenFirstName,
      tokenLastName: unboxing.tokenLastName,
      tokenCompany: unboxing.tokenCompany,
      tokenSignature: unboxing.tokenSignature,
      showGiftMessage: unboxing.showGiftMessage,
      giftMessageText: unboxing.giftMessageText,
      skipUnboxing: unboxing.skipUnboxing,
      customVideoUrl: unboxing.customVideoUrl,
    },
    landing: {
      headline: landing.headline,
      subtitle: landing.subtitle,
      showVideo: landing.showVideo,
      videoUrl: landing.videoUrl,
      heroTextMode: landing.heroTextMode,
      heroBgColor: landing.heroBgColor,
      envelopeColor: landing.envelopeColor,
      accentColor: landing.accentColor,
      confettiEnabled: landing.confettiEnabled,
      confettiShape: landing.confettiShape,
      confettiScheme: landing.confettiScheme,
      logoEnabled: landing.logoEnabled,
      logoSynced: landing.logoSynced,
      logoUrl: landing.logoUrl,
      logoOriginalUrl: landing.logoOriginalUrl,
      logoPadding: landing.logoPadding,
      bannerEnabled: landing.bannerEnabled,
      bannerUrl: landing.bannerUrl,
    },
  };
}

interface Step4State {
  rolloverEnabled: boolean;
  selectedCauses: CauseSelection[];
  payAsYouGive: boolean;
  /** Step 4's third funding model. Wins over `payAsYouGive` on the server when true. */
  postpaid: boolean;
}

/** Step 4 — rollover + charity causes (redemption-relevant) + the funding model. The server
 *  inverts rollover to donate_unredeemed and takes the first cause as the landing's featured
 *  beneficiary.
 *
 *  `payAsYouGive` is saved with the DRAFT rather than sent at launch: it decides
 *  `campaigns.campaign_type`, and launch reads the funding model off the campaign row so the
 *  checkout can never contradict what was persisted. The rest of the checkout (apply-credits, the
 *  card) genuinely is launch-only and is NOT sent here. */
export function buildStep4Payload(s: Step4State): BuilderCampaignPayload {
  return {
    rollover: s.rolloverEnabled,
    causes: s.selectedCauses,
    payAsYouGive: s.payAsYouGive,
    postpaid: s.postpaid,
  };
}

/**
 * The campaign name on the wire is `title`. Returns `{ title: <trimmed name> }`, or `{}` when the
 * trimmed name is empty (so the server keeps its default/current title). Never any other key.
 */
export function buildTitlePayload(name: string): BuilderCampaignPayload {
  const title = normalizeCampaignName(name);
  return title ? { title } : {};
}
